import { createSlice } from "@reduxjs/toolkit";
import {
  getAllAdhocJobsAsync,
  getAdhocJobByJobIdAsync,
  getAdhocJobCostAsync,
  getAdhocCostModuleSchemaAsync,
  updateAdhocJobAdminStatusAsync,
  updateAdhocJobTextValidationAsync,
  getAttachableJobsAsync,
  attachMainJobAsync,
  detachMainJobAsync,
  getFumigationDataAsync,
} from "./adhocjob.async";

const initialState = {
  adhocJobsListLoader: false,
  adhocJobsList: {},
  activityViewByJobIdLoading: false,
  activityViewByJobIdInfo: {},
  activityViewByJobId: {},
  costReportLoader: false,
  costReport: null,
  moduleSchemaLoader: false,
  moduleSchema: [],
  adminStatusUpdateLoader: false,
  textValidationUpdateLoader: false,
  attachableJobsLoader: false,
  attachableJobsList: [],
  attachMainJobLoader: false,
  detachMainJobLoader: false,
  fumigationDataLoader: false,
  fumigationData: null,
};

const adhocJobSlice = createSlice({
  name: "adhocjob",
  initialState,
  reducers: {
    emptyAdhocJobs: (state) => {
      state.adhocJobsList = {};
    },
    emptyCostReport: (state) => {
      state.costReport = null;
    },
    emptyAttachableJobs: (state) => {
      state.attachableJobsList = [];
    },
    emptyFumigationData: (state) => {
      state.fumigationData = null;
    },
  },
  extraReducers: (builder) => {
    // Get All Adhoc Jobs
    builder.addCase(getAllAdhocJobsAsync.pending, (state) => {
      state.adhocJobsListLoader = true;
    });
    builder.addCase(getAllAdhocJobsAsync.fulfilled, (state, action) => {
      state.adhocJobsListLoader = false;
      state.adhocJobsList = action.payload;
    });
    builder.addCase(getAllAdhocJobsAsync.rejected, (state) => {
      state.adhocJobsListLoader = false;
    });

    // Update Admin Status
    builder.addCase(updateAdhocJobAdminStatusAsync.pending, (state) => {
      state.adminStatusUpdateLoader = true;
    });
    builder.addCase(
      updateAdhocJobAdminStatusAsync.fulfilled,
      (state, action) => {
        state.adminStatusUpdateLoader = false;
        const updatedJob = action.payload?.data;
        if (updatedJob && state.adhocJobsList?.data?.jobs) {
          const idx = state.adhocJobsList.data.jobs.findIndex(
            (j) => j._id === updatedJob._id
          );
          if (idx !== -1) {
            state.adhocJobsList.data.jobs[idx] = {
              ...state.adhocJobsList.data.jobs[idx],
              adminStatus: updatedJob.adminStatus,
            };
          }
        }
      }
    );
    builder.addCase(updateAdhocJobAdminStatusAsync.rejected, (state) => {
      state.adminStatusUpdateLoader = false;
    });

    // Get Activity View By JobId
    builder.addCase(getAdhocJobByJobIdAsync.pending, (state) => {
      state.activityViewByJobIdLoading = true;
      state.activityViewByJobIdInfo = {};
      state.activityViewByJobId = {};
    });
    builder.addCase(getAdhocJobByJobIdAsync.fulfilled, (state, action) => {
      state.activityViewByJobIdLoading = false;
      state.activityViewByJobIdInfo = {};
      state.activityViewByJobId = action.payload?.data;
    });
    builder.addCase(getAdhocJobByJobIdAsync.rejected, (state) => {
      state.activityViewByJobIdLoading = false;
      state.activityViewByJobIdInfo = {};
      state.activityViewByJobId = {};
    });

    // Get Cost Report
    builder.addCase(getAdhocJobCostAsync.pending, (state) => {
      state.costReportLoader = true;
      state.costReport = null;
    });
    builder.addCase(getAdhocJobCostAsync.fulfilled, (state, action) => {
      state.costReportLoader = false;
      state.costReport = action.payload?.data;
    });
    builder.addCase(getAdhocJobCostAsync.rejected, (state) => {
      state.costReportLoader = false;
      state.costReport = null;
    });

    // Get Cost Module Schema (dynamic, read-only view)
    builder.addCase(getAdhocCostModuleSchemaAsync.pending, (state) => {
      state.moduleSchemaLoader = true;
    });
    builder.addCase(getAdhocCostModuleSchemaAsync.fulfilled, (state, action) => {
      state.moduleSchemaLoader = false;
      state.moduleSchema = action.payload?.data || [];
    });
    builder.addCase(getAdhocCostModuleSchemaAsync.rejected, (state) => {
      state.moduleSchemaLoader = false;
      state.moduleSchema = [];
    });

    // Update Text Validation
    builder.addCase(updateAdhocJobTextValidationAsync.pending, (state) => {
      state.textValidationUpdateLoader = true;
    });
    builder.addCase(
      updateAdhocJobTextValidationAsync.fulfilled,
      (state, action) => {
        state.textValidationUpdateLoader = false;
      }
    );
    builder.addCase(updateAdhocJobTextValidationAsync.rejected, (state) => {
      state.textValidationUpdateLoader = false;
    });

    // Get Attachable Jobs
    builder.addCase(getAttachableJobsAsync.pending, (state) => {
      state.attachableJobsLoader = true;
      state.attachableJobsList = [];
    });
    builder.addCase(getAttachableJobsAsync.fulfilled, (state, action) => {
      state.attachableJobsLoader = false;
      state.attachableJobsList = action.payload?.data || [];
    });
    builder.addCase(getAttachableJobsAsync.rejected, (state) => {
      state.attachableJobsLoader = false;
      state.attachableJobsList = [];
    });

    // Attach Main Job — updates the matching row in adhocJobsList in place,
    // same pattern as updateAdhocJobAdminStatusAsync above, instead of a full refetch.
    builder.addCase(attachMainJobAsync.pending, (state) => {
      state.attachMainJobLoader = true;
    });
    builder.addCase(attachMainJobAsync.fulfilled, (state, action) => {
      state.attachMainJobLoader = false;
      const updatedJob = action.payload?.data;
      const idx = state.adhocJobsList?.data?.jobs?.findIndex(
        (j) => j._id === updatedJob?._id
      );
      if (idx > -1) {
        state.adhocJobsList.data.jobs[idx] = {
          ...state.adhocJobsList.data.jobs[idx],
          mainJobId: updatedJob.mainJobId,
          isMainJobAdded: updatedJob.isMainJobAdded,
        };
      }
    });
    builder.addCase(attachMainJobAsync.rejected, (state) => {
      state.attachMainJobLoader = false;
    });

    // Detach Main Job — same in-place update, cleared back to null/false
    builder.addCase(detachMainJobAsync.pending, (state) => {
      state.detachMainJobLoader = true;
    });
    builder.addCase(detachMainJobAsync.fulfilled, (state, action) => {
      state.detachMainJobLoader = false;
      const updatedJob = action.payload?.data;
      const idx = state.adhocJobsList?.data?.jobs?.findIndex(
        (j) => j._id === updatedJob?._id
      );
      if (idx > -1) {
        state.adhocJobsList.data.jobs[idx] = {
          ...state.adhocJobsList.data.jobs[idx],
          mainJobId: null,
          isMainJobAdded: false,
        };
      }
    });
    builder.addCase(detachMainJobAsync.rejected, (state) => {
      state.detachMainJobLoader = false;
    });

    // Get Fumigation Data — read-only, see docs/adhoc-fumigation-visit-flow-plan.md
    builder.addCase(getFumigationDataAsync.pending, (state) => {
      state.fumigationDataLoader = true;
      state.fumigationData = null;
    });
    builder.addCase(getFumigationDataAsync.fulfilled, (state, action) => {
      state.fumigationDataLoader = false;
      state.fumigationData = action.payload?.data;
    });
    builder.addCase(getFumigationDataAsync.rejected, (state) => {
      state.fumigationDataLoader = false;
      state.fumigationData = null;
    });
  },
});

export const { emptyAdhocJobs, emptyCostReport, emptyAttachableJobs, emptyFumigationData } =
  adhocJobSlice.actions;
export default adhocJobSlice.reducer;